import { useState } from 'react';
import { useTenants } from '../hooks/useTenants';
import { useSupportTicketsList } from '../hooks/useSupportTickets';
import type { SupportTicketStatus, SupportTicketTmListItem } from '@policy-tracker/types';

/**
 * TM admin support ticket list (#2304 part 2).
 *
 * Open / Closed tabs across the top; tenant dropdown selects which tenant's
 * tickets to display (the sub-account `/api-tm` endpoint requires a
 * `tenantId` in the path, so a tenant must be picked before any tickets
 * appear). Each row shows tenant name, subject, submitter, last activity,
 * and a status badge. Clicking a row invokes `onSelect(tenantId, ticketId)`
 * so the parent `App.tsx` can swap to the detail view + push history state.
 */
interface SupportTicketListProps {
    onSelect: (tenantId: string, ticketId: string) => void;
}

function StatusBadge({ status }: { status: SupportTicketStatus }) {
    const color = status === 'open' ? '#16a34a' : '#71717a';
    return (
        <span
            style={{
                display: 'inline-block',
                padding: '0.125rem 0.5rem',
                borderRadius: '999px',
                fontSize: '0.75rem',
                color: 'white',
                background: color,
                textTransform: 'capitalize',
            }}
        >
            {status}
        </span>
    );
}

function formatDateTime(iso: string): string {
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
}

export default function SupportTicketList({ onSelect }: SupportTicketListProps) {
    const [tab, setTab] = useState<SupportTicketStatus>('open');
    const [tenantId, setTenantId] = useState<string>('');

    const tenantsQuery = useTenants();
    const ticketsQuery = useSupportTicketsList(tenantId ? { tenantId, status: tab } : undefined);

    const tenants = tenantsQuery.data?.items ?? [];
    const tickets: SupportTicketTmListItem[] = ticketsQuery.data?.items ?? [];

    return (
        <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2>Support Tickets</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn" aria-pressed={tab === 'open'} onClick={() => setTab('open')} style={{ opacity: tab === 'open' ? 1 : 0.6 }}>
                        Open
                    </button>
                    <button className="btn" aria-pressed={tab === 'closed'} onClick={() => setTab('closed')} style={{ opacity: tab === 'closed' ? 1 : 0.6 }}>
                        Closed
                    </button>
                </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="tenant-filter">Tenant:&nbsp;</label>
                <select id="tenant-filter" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                    <option value="">— Select a tenant —</option>
                    {tenants.map((t) => (
                        <option key={t.tenantId} value={t.tenantId}>
                            {t.name}
                        </option>
                    ))}
                </select>
            </div>

            {!tenantId && <p>Select a tenant to view their tickets.</p>}

            {tenantId && ticketsQuery.isLoading && <p>Loading tickets...</p>}

            {tenantId && !ticketsQuery.isLoading && tickets.length === 0 && <p>No {tab} tickets for this tenant.</p>}

            {tickets.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #ccc' }}>Tenant</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #ccc' }}>Subject</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #ccc' }}>Submitter</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #ccc' }}>Last activity</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #ccc' }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tickets.map((t) => (
                            <tr key={t.ticketId} style={{ cursor: 'pointer' }} onClick={() => onSelect(t.tenantId, t.ticketId)}>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>{t.tenantName}</td>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>{t.subject}</td>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>{t.createdByName}</td>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>{formatDateTime(t.updatedAt)}</td>
                                <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>
                                    <StatusBadge status={t.status} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
